import { InputType } from '@agilox/ui-common';
import {
	Component,
	computed,
	ElementRef,
	input,
	model,
	output,
	signal,
	viewChild,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';

@Component({
	selector: 'ui-input',
	templateUrl: './input.component.html',
	standalone: false,
})
export class InputComponent implements FormValueControl<string> {
	type = input<InputType>('text');
	/**
	 * Will show a checkmark icon and color the border in green
	 * if the input is valid.
	 */
	showWhenValid = input<boolean>(false);
	placeholder = input<string>('');
	hideClearIcon = input<boolean>(false);
	inputId = input<string>('');
	styleClass = input<string>('');
	disabled = input<boolean>(false);
	invalid = input<boolean>(false);
	/**
	 * Caps what can be typed, so the limit is reached rather than exceeded and
	 * the field never has to report the overflow as an error.
	 */
	maxLength = input<number | undefined>();

	value = model<string>('');

	/**
	 * Will output anytime the input changes.
	 */
	valueChanged = output<string>();
	touch = output<void>();

	input = viewChild<ElementRef | undefined>('inputElement');

	showError = computed(() => this.invalid() && !!this.value());

	showSuccess = computed(
		() => !this.focused() && !this.invalid() && this.showWhenValid() && !!this.value()
	);

	showClearIcon = computed(
		() =>
			!this.invalid() &&
			(this.showWhenValid() ? !!this.value() : true) &&
			!this.hideClearIcon() &&
			this.focused()
	);

	/**
	 * If the input type is password, this property will be used
	 * to toggle the visibility of the password
	 */
	passwordVisible = signal(false);

	focused = signal(false);

	onInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.value.set(value);
		this.valueChanged.emit(value);
	}

	/**
	 * Clears the input field
	 * and sets the focus to the input because the clear icon
	 * is outside the input
	 */
	clearInput($event: Event) {
		$event.preventDefault();
		this.value.set('');
		this.valueChanged.emit('');
		this.setFocus();
	}

	toggleVisibility() {
		this.passwordVisible.set(!this.passwordVisible());
		this.setFocus();
	}

	onFocusOut() {
		this.focused.set(false);
		this.touch.emit();
	}

	setFocus() {
		if (this.input()?.nativeElement) {
			this.input()?.nativeElement.focus();
		}
	}

	onFocusIn() {
		if (!this.disabled()) {
			this.focused.set(true);
		}
	}
}
